import React, { useState, useEffect, useCallback } from 'react';
import {
    VStack,
    HStack,
    Text,
    Box,
    Divider,
    Tag,
    Button,
    Input,
    Spinner,
    SimpleGrid,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
} from '@chakra-ui/react';

import Const from '../Constants';
import { fsDateToString } from '../Utils';

const StatCard = ({ label, value, isLoading }) => (
    <Box
        background={Const.Gray36}
        borderRadius={Const.MD}
        padding={6}
        minW={'160px'}
        textAlign={Const.sCenter}
        borderWidth={'1px'}
        borderColor={Const.Gray64}
    >
            <Text fontSize={'3xl'} fontWeight={Const.Bold} color={Const.White}>
            {isLoading ? '—' : (value ?? '—')}
        </Text>
        <Text fontSize={Const.SM} color={Const.Gray165} marginTop={1}>
            {label}
        </Text>
    </Box>
);

// Card showing an entity's total plus its trailing-window velocity (24h / 7d / 30d).
const PlatformCard = ({ label, stats, isLoading }) => (
    <Box
        background={Const.Gray36}
        borderRadius={Const.MD}
        padding={6}
        minW={'200px'}
        textAlign={Const.sCenter}
        borderWidth={'1px'}
        borderColor={Const.Gray64}
    >
        <Text fontSize={'3xl'} fontWeight={Const.Bold} color={Const.White}>
            {isLoading ? '—' : (stats?.total ?? '—')}
        </Text>
        <Text fontSize={Const.SM} color={Const.Gray165} marginTop={1}>
            {label}
        </Text>
        <Text fontSize={'xs'} color={Const.Gray180} marginTop={2}>
            {isLoading
                ? '—'
                : `${stats?.last24h ?? 0} · ${stats?.last7Days ?? 0} · ${stats?.last30Days ?? 0}`}
        </Text>
        <Text fontSize={'xs'} color={Const.Gray165}>
            24h · 7d · 30d
        </Text>
    </Box>
);

const FeedbackRow = ({ item }) => {
    const dateStr = item.timestamp ? fsDateToString(item.timestamp) : '';
    return (
        <Box
            background={Const.Gray36}
            borderRadius={Const.MD}
            padding={4}
            borderWidth={'1px'}
            borderColor={Const.Gray64}
            width={'100%'}
            textAlign={Const.Left}
        >
            <HStack spacing={3} alignItems={'baseline'}>
                {item.type && (
                    <Tag size={'sm'} background={Const.Gray64} color={Const.Orange400}>
                        {item.type}
                    </Tag>
                )}
                <Text fontSize={Const.MD} fontWeight={Const.Bold} color={Const.White} noOfLines={1}>
                    {item.title || '(no title)'}
                </Text>
            </HStack>
            <HStack spacing={2} marginTop={1}>
                <Text fontSize={Const.SM} color={Const.Gray165}>
                    {item.userEmail || 'unknown'}
                </Text>
                <Text fontSize={Const.SM} color={Const.Gray180}>·</Text>
                <Text fontSize={Const.SM} color={Const.Gray180}>{dateStr}</Text>
            </HStack>
            {item.body && (
                <Text
                    fontSize={Const.SM}
                    color={Const.Gray180}
                    marginTop={2}
                    noOfLines={3}
                    title={item.body}
                    whiteSpace={'pre-wrap'}
                >
                    {item.body}
                </Text>
            )}
        </Box>
    );
};

const UserRow = ({ user, onClick }) => (
    <Box
        as={'button'}
        onClick={onClick}
        background={Const.Gray36}
        borderRadius={Const.MD}
        padding={4}
        borderWidth={'1px'}
        borderColor={Const.Gray64}
        width={'100%'}
        textAlign={Const.Left}
        _hover={{ borderColor: Const.Orange400 }}
    >
        <HStack justifyContent={'space-between'} alignItems={'baseline'}>
            <Text fontSize={Const.MD} fontWeight={Const.Bold} color={Const.White} noOfLines={1}>
                {user.email || 'unknown'}
            </Text>
            {user.role && (
                <Tag size={'sm'} background={Const.Gray64} color={Const.Gray165}>
                    {user.role}
                </Tag>
            )}
        </HStack>
        <HStack spacing={2} marginTop={1}>
            <Text fontSize={Const.SM} color={Const.Gray165}>
                Joined {user.timestamp ? fsDateToString(user.timestamp) : '—'}
            </Text>
            <Text fontSize={Const.SM} color={Const.Gray180}>·</Text>
            <Text fontSize={Const.SM} color={Const.Gray180}>
                Active {user.lastSignInAt ? fsDateToString(user.lastSignInAt) : '—'}
            </Text>
        </HStack>
    </Box>
);

const Admin = ({ auth }) => {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [order, setOrder] = useState('active'); // 'active' | 'signup'
    const [query, setQuery] = useState('');

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const authedFetch = useCallback(async (path) => {
        const idToken = await auth.currentUser.getIdToken();
        return fetch(`${process.env.REACT_APP_BASE_URL}${path}`, {
            headers: new Headers({ Authorization: idToken })
        });
    }, [auth]);

    // Platform + feedback stats (single round-trip).
    useEffect(() => {
        async function loadStats() {
            try {
                const response = await authedFetch('/admin/stats');
                if (response.ok) {
                    setStats(await response.json());
                }
            } catch (error) {
                console.error(error.message);
            } finally {
                setIsLoading(false);
            }
        }
        loadStats();
    }, [authedFetch]);

    // Users list — re-queries on order change or (debounced) search.
    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            setUsersLoading(true);
            try {
                const q = query.trim();
                const path = q
                    ? `/admin/users?q=${encodeURIComponent(q)}`
                    : `/admin/users?order=${order}`;
                const response = await authedFetch(path);
                if (response.ok && !cancelled) {
                    const data = await response.json();
                    setUsers(data.users ?? []);
                }
            } catch (error) {
                console.error(error.message);
            } finally {
                if (!cancelled) setUsersLoading(false);
            }
        }, query.trim() ? 300 : 0);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [authedFetch, order, query]);

    const openUserDetail = async (userId) => {
        setDetail(null);
        setDetailLoading(true);
        onOpen();
        try {
            const response = await authedFetch(`/admin/users/${userId}`);
            if (response.ok) {
                setDetail(await response.json());
            }
        } catch (error) {
            console.error(error.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const recent = stats?.feedback?.recent ?? [];
    const platform = stats?.platform;
    const detailCounts = detail?.counts;

    const ToggleButton = ({ value, children }) => (
        <Button
            size={'sm'}
            variant={order === value ? 'solid' : 'outline'}
            background={order === value ? Const.Orange400 : 'transparent'}
            color={order === value ? Const.Gray36 : Const.Gray165}
            borderColor={Const.Gray64}
            _hover={{ borderColor: Const.Orange400 }}
            onClick={() => setOrder(value)}
            isDisabled={!!query.trim()}
        >
            {children}
        </Button>
    );

    return (
        <Box height={'100vh'} width={'100%'} overflowY={'auto'}>
        <VStack padding={8} w={'100%'} alignItems={'flex-start'} spacing={6}>
            <Text fontSize={'xl'} fontWeight={Const.Bold} color={Const.White}>
                Dashboard
            </Text>
            <Divider borderColor={Const.Gray64} />

            <Text fontSize={Const.SM} fontWeight={Const.Bold} color={Const.Gray165}>
                USER ACTIVITY
            </Text>

            <HStack spacing={4} flexWrap={'wrap'}>
                    <StatCard label={'Logged in last 24h'} value={stats?.loggedInLast24h} isLoading={isLoading} />
                    <StatCard label={'Logged in last 7 days'} value={stats?.loggedInLast7Days} isLoading={isLoading} />
                    <StatCard label={'Logged in last 30 days'} value={stats?.loggedInLast30Days} isLoading={isLoading} />
                    <StatCard label={'Total users'} value={stats?.totalUsers} isLoading={isLoading} />
            </HStack>

            <Divider borderColor={Const.Gray64} />

            <Text fontSize={Const.SM} fontWeight={Const.Bold} color={Const.Gray165}>
                PLATFORM ACTIVITY (TOTAL · 24h / 7d / 30d)
            </Text>

            <HStack spacing={4} flexWrap={'wrap'}>
                <PlatformCard label={'Tasks'} stats={platform?.tasks} isLoading={isLoading} />
                <PlatformCard label={'Notes'} stats={platform?.notes} isLoading={isLoading} />
                <PlatformCard label={'Meetings'} stats={platform?.meetings} isLoading={isLoading} />
                <PlatformCard label={'One-on-ones'} stats={platform?.oneOnOnes} isLoading={isLoading} />
                <PlatformCard label={'Interviews'} stats={platform?.interviews} isLoading={isLoading} />
            </HStack>

            <Divider borderColor={Const.Gray64} />

            <Text fontSize={Const.SM} fontWeight={Const.Bold} color={Const.Gray165}>
                USERS
            </Text>

            <HStack spacing={3} flexWrap={'wrap'}>
                <ToggleButton value={'active'}>Recently active</ToggleButton>
                <ToggleButton value={'signup'}>Newest signups</ToggleButton>
                <Input
                    size={'sm'}
                    width={'280px'}
                    placeholder={'Search by email…'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    background={Const.Gray36}
                    borderColor={Const.Gray64}
                    color={Const.White}
                    _placeholder={{ color: Const.Gray165 }}
                />
            </HStack>

            {usersLoading ? (
                <Text fontSize={Const.SM} color={Const.Gray165}>Loading…</Text>
            ) : users.length === 0 ? (
                <Text fontSize={Const.SM} color={Const.Gray165}>No users found.</Text>
            ) : (
                <VStack
                    spacing={3}
                    alignItems={'stretch'}
                    width={'100%'}
                    maxW={'820px'}
                    maxH={'420px'}
                    overflowY={'auto'}
                    paddingRight={2}
                >
                    {users.map((user) => (
                        <UserRow key={user.id} user={user} onClick={() => openUserDetail(user.id)} />
                    ))}
                </VStack>
            )}

            <Divider borderColor={Const.Gray64} />

            <Text fontSize={Const.SM} fontWeight={Const.Bold} color={Const.Gray165}>
                RECENT FEEDBACK
            </Text>

            {isLoading ? (
                <Text fontSize={Const.SM} color={Const.Gray165}>Loading…</Text>
            ) : recent.length === 0 ? (
                <Text fontSize={Const.SM} color={Const.Gray165}>No feedback yet.</Text>
            ) : (
                <VStack
                    spacing={3}
                    alignItems={'stretch'}
                    width={'100%'}
                    maxW={'820px'}
                    maxH={'420px'}
                    overflowY={'auto'}
                    paddingRight={2}
                >
                    {recent.map((item) => (
                        <FeedbackRow key={item.id} item={item} />
                    ))}
                </VStack>
            )}

            <Modal isOpen={isOpen} onClose={onClose} isCentered size={'xl'}>
                <ModalOverlay />
                <ModalContent background={Const.Gray36} borderWidth={'1px'} borderColor={Const.Gray64}>
                    <ModalHeader color={Const.White}>
                        {detail?.user?.email || 'User activity'}
                    </ModalHeader>
                    <ModalCloseButton color={Const.Gray165} />
                    <ModalBody paddingBottom={6}>
                        {detailLoading ? (
                            <HStack><Spinner size={'sm'} color={Const.Orange400} /><Text color={Const.Gray165}>Loading…</Text></HStack>
                        ) : detailCounts ? (
                            <VStack alignItems={'stretch'} spacing={4}>
                                <Text fontSize={Const.XS} color={Const.Gray180} fontFamily={'mono'} userSelect={'all'}>
                                    {detail.user.id}
                                </Text>
                                <Text fontSize={Const.SM} color={Const.Gray165}>
                                    Joined {detail.user.signupAt ? fsDateToString(detail.user.signupAt) : '—'}
                                    {' · '}
                                    Active {detail.user.lastActiveAt ? fsDateToString(detail.user.lastActiveAt) : '—'}
                                </Text>
                                <SimpleGrid columns={3} spacing={3}>
                                    <StatCard label={'Tasks'} value={detailCounts.tasks} />
                                    <StatCard label={'Notes'} value={detailCounts.notes} />
                                    <StatCard label={'Meetings'} value={detailCounts.meetings} />
                                    <StatCard label={'One-on-ones'} value={detailCounts.oneOnOnes} />
                                    <StatCard label={'Interviews'} value={detailCounts.interviews} />
                                    <StatCard label={'Agent runs'} value={detailCounts.agentRuns} />
                                </SimpleGrid>
                            </VStack>
                        ) : (
                            <Text color={Const.Gray165}>Could not load user activity.</Text>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </VStack>
        </Box>
    );
};

export default Admin;
